import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TeamCompaniesService, ThatchEmployerSetup } from '../../services/teamcompanies/teamcompanies.service';

@Component({
  selector: 'app-thatch-help-dialog',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, FormsModule, MatCheckboxModule],
  template: `
  <h2 mat-dialog-title id="thatch-help-title">How Thatch works</h2>
  <mat-dialog-content class="thatch-help-content">
    <p>Set up your company's health benefits in Thatch, then follow employee enrollments in Wallit.</p>
    <ol>
      <li><strong>Set up your employer account.</strong> Sign up with Thatch through Wallit's partner link and complete your employer setup.</li>
      <li><strong>Invite your employees.</strong> Send employee invitations from Thatch.</li>
      <li><strong>Employees enroll in Thatch.</strong> Employees use their invitations to create their Thatch accounts and choose their benefits on the Thatch platform.</li>
      <li><strong>Wallit syncs enrollment data.</strong> Wallit periodically reads Thatch enrollment data and links it to the matching employee accounts in Wallit.</li>
    </ol>
    <p>Use the same employee email addresses in Thatch and Wallit so enrollments can be matched. Updates may take time to appear.</p>
    @if (loading) { <p role="status">Loading employer setup…</p> }
    @if (setup) {
      <h3>{{ setup.companyName }}</h3>
      @if (setup.method === 'existing_access') {
        <p role="status">Existing access preserved. No additional confirmation is required.</p>
      } @else if (setup.method === 'employer_confirmed') {
        <p role="status">Employer-confirmed. Wallit has recorded your company's confirmation; this is not verification by Thatch.</p>
      } @else if (setup.required) {
        <p>Complete employer setup in Thatch, then return here to confirm. Confirmation applies to this company and does not select benefits or enroll employees.</p>
        @if (setup.partnerUrl) {
          <p><a mat-stroked-button [href]="setup.partnerUrl" target="_blank" rel="noopener noreferrer">Set up Thatch (opens in a new tab)</a></p>
        }
        <mat-checkbox [(ngModel)]="confirmed" [disabled]="saving">{{ setup.confirmationText }}</mat-checkbox>
        <p>Your confirmation is saved separately from benefit selections, even if you later cancel the benefits editor.</p>
      }
    }
    @if (error) { <p role="alert">{{ error }}</p> }
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    @if (error && !setup) { <button mat-button type="button" (click)="load()" [disabled]="loading">Retry</button> }
    <button mat-button type="button" [disabled]="saving" [mat-dialog-close]="setup">Close</button>
    @if (setup?.required && !setup?.method) {
      <button mat-flat-button type="button" (click)="confirm()" [disabled]="!confirmed || saving">{{ saving ? 'Saving…' : 'Confirm employer setup' }}</button>
    }
  </mat-dialog-actions>
  `,
  styles: [`
    .thatch-help-content { line-height: 1.5; }
    ol { padding-left: 24px; }
    li + li { margin-top: 12px; }
  `],
})
export class ThatchHelpDialogComponent implements OnInit {
  setup: ThatchEmployerSetup | null = null;
  confirmed = false;
  loading = false;
  saving = false;
  error = '';
  constructor(@Inject(MAT_DIALOG_DATA) public data: {companyId?: string} | null,
    private companies: TeamCompaniesService,
    private ref: MatDialogRef<ThatchHelpDialogComponent>) {}
  ngOnInit(): void { void this.load(); }
  async load(): Promise<void> {
    if (!this.data?.companyId) return;
    this.loading = true;
    this.error = '';
    try { this.setup = await this.companies.getThatchEmployerSetup(this.data.companyId); }
    catch { this.error = 'Unable to load employer setup. A company administrator can confirm setup here. Please retry or contact your administrator.'; }
    finally { this.loading = false; }
  }
  async confirm(): Promise<void> {
    if (!this.confirmed || this.saving || !this.data?.companyId) return;
    this.saving = true;
    this.ref.disableClose = true;
    this.error = '';
    try {
      this.setup = await this.companies.confirmThatchEmployerSetup(this.data.companyId);
      this.ref.close(this.setup);
    } catch { this.error = 'Your confirmation could not be saved. Please retry. Thatch has not been selected.'; }
    finally { this.saving = false; this.ref.disableClose = false; }
  }
}
